/**
 * Map Bundle service for API communication with map bundle endpoints.
 *
 * Supports multiple bundles per event, each with its own name, AoO bounds,
 * and optional faction scope.
 */

import apiClient from '../api/client';
import { MapBundle, CreateBundlePayload } from '../types/mapBundle';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * Build a base URL for backend endpoints mounted at the app root
 * (not under `/api/v1`). Strips a trailing `/api/v1` from the configured
 * API URL so endpoints like `/download/{id}` and `/thumbnail/{id}` resolve
 * correctly regardless of whether REACT_APP_API_URL includes the suffix.
 *
 * Mirrors the equivalent helper in the Android client
 * (ft-android/.../MapBundleRepository.kt).
 */
const getRootBaseUrl = (): string => {
    let baseUrl = API_URL;
    if (baseUrl.endsWith('/')) {
        baseUrl = baseUrl.slice(0, -1);
    }
    if (baseUrl.endsWith('/api/v1')) {
        baseUrl = baseUrl.slice(0, -'/api/v1'.length);
    }
    if (baseUrl.endsWith('/')) {
        baseUrl = baseUrl.slice(0, -1);
    }
    return baseUrl;
};

export const mapBundleService = {
    /**
     * List all map bundles for an event, optionally filtered by faction.
     */
    async listBundles(eventId: string, factionId?: string): Promise<MapBundle[]> {
        const params = factionId ? { faction_id: factionId } : undefined;
        const response = await apiClient.get<{ bundles: MapBundle[]; total: number }>(
            `/api/v1/events/${eventId}/map-bundles`,
            { params },
        );
        return response.data.bundles;
    },

    /**
     * Get a single map bundle by ID.
     */
    async getBundle(eventId: string, bundleId: string): Promise<MapBundle> {
        const response = await apiClient.get<MapBundle>(
            `/api/v1/events/${eventId}/map-bundles/${bundleId}`,
        );
        return response.data;
    },

    /**
     * Create a new map bundle for an event.
     */
    async createBundle(eventId: string, payload: CreateBundlePayload): Promise<MapBundle> {
        const response = await apiClient.post<MapBundle>(
            `/api/v1/events/${eventId}/map-bundles`,
            payload,
        );
        return response.data;
    },

    /**
     * Delete a map bundle.
     */
    async deleteBundle(eventId: string, bundleId: string): Promise<void> {
        await apiClient.delete(`/api/v1/events/${eventId}/map-bundles/${bundleId}`);
    },

    /**
     * Get the public download URL for a map bundle.
     * No authentication needed — uses the public /download/{bundleId} endpoint.
     */
    getDownloadUrl(bundleId: string): string {
        return `${getRootBaseUrl()}/download/${bundleId}`;
    },

    /**
     * Get the public thumbnail URL for a map bundle.
     * No authentication needed — uses the public /thumbnail/{bundleId} endpoint.
     * Returns 404 if the bundle is not ready or has no thumbnail.
     */
    getThumbnailUrl(bundleId: string): string {
        return `${getRootBaseUrl()}/thumbnail/${bundleId}`;
    },
};

export default mapBundleService;
